import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Box, Button, Grid, Typography, useMediaQuery } from "@mui/material";
import ThemedDatePicker from "../../utils/ThemedDatePicker";
import ThemedSelect from "../../utils/ThemedSelect";
import ButtonBack from "../../utils/ButtonBack";
import ErrorModal from "../../utils/ErrorModal";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#DDE4EC";
const FIELD_BG = "#F8FAFC";
const PRIMARY = "#0B5C8A";
const PRIMARY_DARK = "#084767";
const ERROR = "#C42B24";

// Section accent dots
const SEC_COLORS = {
  organization: "#2F7FD1",
  admin: "#12A08F",
  subscription: "#6D5DE8",
};

/* ---------- shared visual styles (values only — no logic) ---------- */
const card = { background: "#FFFFFF", border: `1px solid ${LINE}`, borderRadius: 14, marginBottom: 14 };
const cardHead = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "13px 16px", borderBottom: `1px solid ${LINE}` };
const cardTitle = { display: "flex", alignItems: "center", gap: 9, fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.11em", textTransform: "uppercase", color: INK_2 };
const dot = (c) => ({ width: 6, height: 6, flex: "0 0 6px", borderRadius: "50%", background: c });
const cardMeta = { fontFamily: FONT, fontSize: 12, color: MUTED };
const labelStyle = { display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: 6, fontFamily: FONT, fontSize: 12, fontWeight: 600, color: INK };
const req = { color: ERROR, marginLeft: 3 };
const inputStyle = (hasError, mono) => ({
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  height: 36,
  padding: "0 12px",
  margin: 0,
  background: FIELD_BG,
  border: `1px solid ${hasError ? ERROR : FIELD_EDGE}`,
  borderRadius: 8,
  fontFamily: mono ? MONO : FONT,
  fontSize: mono ? 13 : 13.5,
  color: INK,
  outline: "none",
});
const errorStyle = { display: "block", margin: "4px 0 0", fontFamily: FONT, fontSize: 11.5, fontWeight: 500, color: ERROR };
const primaryBtn = {
  height: "38px !important", minHeight: "38px !important", px: 2, margin: "0 !important", borderRadius: "10px !important",
  background: `${PRIMARY} !important`, color: "#FFFFFF !important", border: "0 !important",
  fontFamily: FONT, fontSize: 14, fontWeight: 600, textTransform: "none", boxShadow: "none", whiteSpace: "nowrap",
  "&:hover": { background: `${PRIMARY_DARK} !important` }, "&.Mui-disabled": { opacity: 0.6 },
};
const ghostBtn = {
  height: "38px !important", minHeight: "38px !important", px: 2, margin: "0 !important", borderRadius: "10px !important",
  background: "#FFFFFF !important", color: `${INK} !important`, border: `1px solid ${FIELD_EDGE} !important`,
  fontFamily: FONT, fontSize: 14, fontWeight: 600, textTransform: "none", boxShadow: "none",
  "&:hover": { background: "#F4F7FA !important" },
};
const cols = (n) => ({
  display: "grid",
  gap: "16px 12px",
  gridTemplateColumns: { xs: "minmax(0,1fr)", sm: "repeat(2, minmax(0,1fr))", lg: `repeat(${n}, minmax(0,1fr))` },
});

// Scoped resets so global App.css rules (form, border-container,
// phone-container, inputs, labels) can't squeeze or restyle this page.
const SCOPED_CSS = `
.dc-org .border-container,.dc-org form.form,.dc-org .dashboard-container{width:100%!important;max-width:none!important;margin:0!important;padding:0!important;box-shadow:none!important;background:transparent!important;border:0!important;box-sizing:border-box!important;}
.dc-org .dc-card{width:100%!important;max-width:none!important;margin:0 0 14px 0!important;padding:0!important;box-shadow:none!important;box-sizing:border-box!important;}
.dc-org .dc-card label{width:auto!important;margin-top:0!important;font-size:12px!important;line-height:1.4!important;}
.dc-org .dc-card input{width:100%!important;max-width:none!important;margin:0!important;box-sizing:border-box!important;height:36px!important;font-size:13.5px!important;}
.dc-org .dc-card .phone-container{width:100%!important;max-width:none!important;margin:0!important;padding:0!important;}
.dc-org .dc-card input.dc-bare{height:100%!important;border:0!important;background:transparent!important;flex:1 1 auto;min-width:0;}
.dc-org .dc-card input::placeholder{color:#8A97A8;opacity:1;}
.dc-org .dc-cc *{font-family:'Plus Jakarta Sans',system-ui,sans-serif!important;font-size:13.5px!important;color:#0F1B2A;}
.dc-org .dc-card p.error{margin:4px 0 0!important;}
.dc-org .dc-head-btns{display:flex!important;flex-direction:row!important;margin:0!important;padding:0!important;align-items:center!important;align-self:flex-end!important;}
.dc-org .dc-head-btns .MuiButton-root,.dc-org .dc-head-btns button{margin:0!important;height:38px!important;min-height:38px!important;max-height:38px!important;padding-top:0!important;padding-bottom:0!important;line-height:1!important;position:static!important;inset:auto!important;top:auto!important;bottom:auto!important;transform:none!important;translate:none!important;align-self:center!important;margin-block:0!important;vertical-align:middle!important;box-sizing:border-box!important;}
`;

const EditOrg = () => {
  const [org, setOrg] = useState();
  const user = JSON.parse(localStorage.getItem("user"));
  const { orgId } = useParams();
  const [errors, setErrors] = useState("");
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 750px)");
  const [btnLoading, setBtnLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState("");

  const handleChange = (e) => {
    const updatedValue =
      e.target.name === "pan" ? e.target.value.toUpperCase() : e.target.value;
    setOrg({ ...org, [e.target.name]: updatedValue });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validateForm = () => {
    const newErrors = {};
    const data = org || {};

    const isEmpty = (value) => !value || value.trim?.() === "";

    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startDate = data.subscriptionStartDate
      ? new Date(data.subscriptionStartDate)
      : null;
    const endDate = data.subscriptionEndDate
      ? new Date(data.subscriptionEndDate)
      : null;

    if (isEmpty(data.orgName)) newErrors.orgName = "Org Name is required";

    if (isEmpty(data.pan)) {
      newErrors.pan = "PAN is required";
    } else if (!panRegex.test(data.pan)) {
      newErrors.pan = "Invalid PAN format (e.g. ABCDE1234F)";
    }

    if (!startDate) {
      newErrors.subscriptionStartDate = "Subscription Start Date is required";
    }

    if (!endDate) {
      newErrors.subscriptionEndDate = "Subscription End Date is required";
    } else if (startDate && endDate <= startDate) {
      newErrors.subscriptionEndDate = "End date must be after start date";
    }

    if (isEmpty(data.subscription)) {
      newErrors.subscription = "Subscription Type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/organization?orgId=${org.orgId}`,
        {
          orgName: org.orgName,
          pan: org.pan,
          subscriptionStartDate: org.subscriptionStartDate,
          subscriptionEndDate: org.subscriptionEndDate,
          adminEmail: null,
          subscription: org.subscription,
        },
        { headers: { Authorization: `Bearer ${user.accessToken}` } }
      );
      navigate("/superadmin-dashboard");
    } catch (error) {
      console.log(error);
      // FIX: tell the user the update failed (was silent before)
      setModalMsg(error?.response?.data?.Exception || "Failed to update organization");
      setModalOpen(true);
    } finally {
      setBtnLoading(false);
    }
  };

  useEffect(() => {
    if (orgId !== undefined) {
      const getOrg = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/organization?orgId=${orgId}`,
            { headers: { Authorization: `Bearer ${user.accessToken}` } }
          );
          setOrg(res.data);
        } catch (error) {
          console.log(error);
        }
      };

      getOrg();
    }
  }, []);

  const handleBack = () => {
    navigate("/superadmin-dashboard");
  };

  /* ---------- markup helpers (same handlers as before) ---------- */
  const fieldBox = (id, label, required, control, err) => (
    <Box key={id}>
      <label htmlFor={id} style={labelStyle}>
        {label}
        {required && <span style={req}>*</span>}
      </label>
      {control}
      {err && <p className="error" style={errorStyle}>{err}</p>}
    </Box>
  );

  const section = (id, title, meta, n, children) => (
    <section key={id} className="dc-card" style={card}>
      <div style={cardHead}>
        <span style={cardTitle}>
          <span style={dot(SEC_COLORS[id])} />
          {title}
        </span>
        <span style={cardMeta}>{meta}</span>
      </div>
      <Box sx={{ p: "16px 16px 18px" }}>
        <Box sx={cols(n)}>{children}</Box>
      </Box>
    </section>
  );

  const saveButton = (
    <Button
      className="btn-contained"
      onClick={handleUpdate}
      disabled={btnLoading}
      sx={primaryBtn}
    >
      {btnLoading ? "Updating..." : "Update Organization"}
    </Button>
  );

  return (
    <>
      <style>{SCOPED_CSS}</style>
      <div
        className="dc-org"
        style={{ fontFamily: FONT, color: INK, textAlign: "left", padding: isMobile ? "4px 0 88px" : "4px 0 32px" }}
      >
        {/* ---------------- PAGE HEADER ---------------- */}
        <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2, mb: 2.5 }}>
          <div style={{ minWidth: 0 }}>
            <button
              type="button"
              onClick={handleBack}
              style={{ border: 0, background: "transparent", padding: 0, marginBottom: 14, cursor: "pointer", fontFamily: FONT, fontSize: 13, fontWeight: 600, color: PRIMARY }}
            >
              ← Organizations
            </button>
            <Typography
              component="h1"
              sx={{ m: 0, fontFamily: FONT, fontSize: isMobile ? 24 : 28, fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.15, color: INK }}
            >
              Edit organization
            </Typography>
            <div style={{ marginTop: 6, fontSize: 13.5, color: INK_2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {org?.orgName || "Organization and subscription details"}
            </div>
          </div>
          {!isMobile && (
            <Box className="dc-head-btns" sx={{ display: "flex", alignItems: "center", gap: 1.25, flex: "0 0 auto", m: "0 !important", p: "0 !important" }}>
              <Button className="btn-outlined" onClick={handleBack} sx={ghostBtn}>
                Cancel
              </Button>
              {saveButton}
            </Box>
          )}
        </Box>

        <div className="dashboard-container" style={{ width: "100%", margin: 0, padding: 0, boxShadow: "none", background: "transparent" }}>
          {/* ---------------- ORGANIZATION ---------------- */}
          {section("organization", "Organization", "2 required", 3, [
            fieldBox(
              "orgName",
              "Org Name",
              true,
              <input
                type="text"
                id="orgName"
                name="orgName"
                placeholder="Organization name"
                onChange={handleChange}
                value={org && org.orgName}
                className={`${errors.orgName && "input-error"}`}
                style={inputStyle(!!errors.orgName)}
              />,
              errors.orgName,
            ),
            fieldBox(
              "pan",
              "PAN",
              true,
              <input
                type="text"
                id="pan"
                name="pan"
                placeholder="ABCDE1234F"
                onChange={handleChange}
                value={org && org.pan}
                className={`${errors.pan && "input-error"}`}
                style={{ ...inputStyle(!!errors.pan, true), letterSpacing: "0.08em" }}
              />,
              errors.pan,
            ),
          ])}

          {/* ---------------- SUBSCRIPTION ---------------- */}
          {section("subscription", "Subscription", "3 required", 3, [
            fieldBox(
              "subscriptionStartDate",
              "Subscription Start Date",
              true,
              <ThemedDatePicker
                id="subscriptionStartDate"
                name="subscriptionStartDate"
                onChange={handleChange}
                value={org && org.subscriptionStartDate}
                error={!!errors.subscriptionStartDate}
              />,
              errors.subscriptionStartDate,
            ),
            fieldBox(
              "subscriptionEndDate",
              "Subscription End Date",
              true,
              <ThemedDatePicker
                id="subscriptionEndDate"
                name="subscriptionEndDate"
                onChange={handleChange}
                value={org && org.subscriptionEndDate}
                error={!!errors.subscriptionEndDate}
                min={org && org.subscriptionStartDate}
              />,
              errors.subscriptionEndDate,
            ),
            fieldBox(
              "subscription",
              "Subscription Type",
              true,
              <ThemedSelect
                id="subscription"
                name="subscription"
                onChange={handleChange}
                value={org && org.subscription}
                error={!!errors.subscription}
                placeholder="---Choose Type---"
                options={[
                  { value: "FREE", label: "Free" },
                  { value: "BASIC", label: "Basic" },
                  { value: "PRO", label: "Pro" },
                ]}
              />,
              errors.subscription,
            ),
          ])}
        </div>

        {/* ---------------- MOBILE ACTION BAR ---------------- */}
        {isMobile && (
          <Box
            className="btn-container"
            sx={{
              position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 30, display: "flex", alignItems: "center", gap: 1.25,
              p: "10px 16px", background: "#FFFFFF", borderTop: `1px solid ${LINE}`, boxShadow: "0 -8px 24px -16px rgba(15,27,42,0.3)",
            }}
          >
            <ButtonBack handleBack={handleBack} />
            <Box sx={{ flex: 1, display: "flex", "& > *": { flex: 1 } }}>{saveButton}</Box>
          </Box>
        )}

        <ErrorModal
          open={modalOpen}
          handleClose={() => setModalOpen(false)}
          msg={modalMsg}
          type="error"
        />
      </div>
    </>
  );
};

export default EditOrg;